import * as React from "react";

import { cn } from "@/engine/support/classNames";

import { ANIMAL_FIGURES } from "./animalFigures";
import {
  colourValue,
  type AnimalId,
  type ColourId,
  type PlaceItemId,
} from "./campData";
import { PlaceProps } from "./PlaceProps";

export interface AnimalFigureProps extends React.HTMLAttributes<HTMLDivElement> {
  animal: AnimalId;
  colour?: ColourId | undefined;
  place?: Partial<Record<"near" | "side" | "air", PlaceItemId>> | undefined;
  pose?: "stand" | "talk" | "sit" | undefined;
  dim?: boolean | undefined;
}

/**
 * An animal casts its person's colour: a thin rim that keeps the outline on any
 * ground, and a soft shadow below it in the same hue — never a grey one.
 */
function figureShadow(identity: string): React.CSSProperties {
  return {
    filter: `drop-shadow(0 0 2px ${identity}) drop-shadow(0 8px 14px color-mix(in srgb, ${identity} 45%, transparent))`,
  };
}

/** A full-height illustrated animal anchored by its feet to the scene ground. */
export const AnimalFigure = React.forwardRef<HTMLDivElement, AnimalFigureProps>(
  function AnimalFigure(
    {
      animal,
      colour = "ash",
      place,
      pose = "stand",
      dim = false,
      className,
      ...props
    },
    ref,
  ) {
    const identity = colourValue(colour);
    return (
      <div
        ref={ref}
        data-pose={pose}
        className={cn(
          "relative h-[132px] w-[100px] overflow-visible transition",
          dim && "saturate-50 opacity-45",
          pose === "talk" && "animate-bounce-subtle",
          pose === "sit" && "scale-y-95 origin-bottom",
          className,
        )}
        {...props}
      >
        {place ? <PlaceProps slots={place} /> : null}
        <span
          className="absolute bottom-1 left-1/2 h-3 w-14 -translate-x-1/2 rounded-full blur-sm"
          style={{
            backgroundColor: `color-mix(in srgb, ${identity} 48%, transparent)`,
          }}
        />
        <img
          src={ANIMAL_FIGURES[animal]}
          alt=""
          className="absolute bottom-0 left-1/2 z-10 h-full w-auto max-w-full -translate-x-1/2 object-contain object-bottom"
          style={figureShadow(identity)}
        />
      </div>
    );
  },
);
